/* eslint-disable max-lines */
import React, {
    useEffect, useMemo, useState
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    Button
} from 'shared/components/ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from 'shared/components/ui/dialog';
import {
    Loader2, Plus, Pencil, Cog, Check, RefreshCw, AlertCircle,
    Play, Maximize2, Minimize2, Code
} from 'lucide-react';
import {
    cn
} from 'shared/lib/utils';
import {
    mcpConnectionService
} from 'shared/api/services/BEX/mcpConnection.service';
import {
    mcpDiscoveryService
} from 'shared/api/services/BEX/mcpDiscovery.service';
import MCPForm from 'builders/builderInternal/components/MCPManager/MCPForm';
import McpTestPanel from '../../components/McpTestPanel';
import EntityJsonView from '../../components/EntityJsonView';

/**
 * McpConnectionPicker — chip with Change / Edit / + New popups for an MCP
 * connection on an opcode `tool` step (protocol="mcp"). Wraps the existing
 * MCPForm in a Dialog so the same form (with its Vault-backed save flow)
 * runs inline. Saves through `mcpConnectionService` so anything
 * created/edited shows up in Library → MCP Servers automatically.
 *
 * Props:
 *   connectionId (string)            — currently selected connection_id
 *   onChange ({connection_id, ...})  — fires with the picked / saved record
 */

const EMPTY_DRAFT = {
    connection_name: '',
    description: '',
    server_url: '',
    transport: 'streamable_http',
    timeout_ms: 30000,
    auth: {
        type: 'none'
    },
    headers: {}
};

export default function McpConnectionPicker({
    connectionId, onChange
}) {
    const {
        oragID, subOragID
    } = useParams();
    const [list, setList] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [error, setError] = useState(null);

    const [pickerOpen, setPickerOpen] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [formMode, setFormMode] = useState('create'); // 'create' | 'edit'
    const [formDraft, setFormDraft] = useState(EMPTY_DRAFT);
    const [editingConnection, setEditingConnection] = useState(null);
    const [showTest, setShowTest] = useState(true);
    const [testMaximized, setTestMaximized] = useState(false);
    const [showJson, setShowJson] = useState(false);

    const initialSnapshot = useMemo(
        () => JSON.stringify(editingConnection || EMPTY_DRAFT),
        [editingConnection]
    );
    const currentSnapshot = JSON.stringify(formDraft || {});
    const pristine = currentSnapshot === initialSnapshot;
    const mcpForTest = editingConnection || {
        ...formDraft,
        connection_id: editingConnection?.connection_id || null
    };

    useEffect(() => {
        if (!formOpen) {
            setTestMaximized(false);
        } else {
            setShowTest(true);
        }
    }, [formOpen]);

    async function refresh() {
        if (!oragID || !subOragID) return;
        setLoadingList(true);
        setError(null);
        try {
            const arr = await mcpConnectionService.listMCPConnections(oragID, subOragID);
            setList(Array.isArray(arr) ? arr : []);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load MCP connections.');
        } finally {
            setLoadingList(false);
        }
    }

    useEffect(() => { refresh(); }, [oragID, subOragID]);

    const selected = list.find(c => c.connection_id === connectionId);

    // Live reachability ping. Uses discovery as a health check — if the
    // server lists tools, the connection is reachable. 'idle' before a ping
    // attempt completes; 'ok' / 'down' after.
    const [health, setHealth] = useState('idle');
    useEffect(() => {
        if (!connectionId || !oragID || !subOragID) {
            setHealth('idle');
            return;
        }
        let cancelled = false;
        setHealth('checking');
        mcpDiscoveryService
            .discover({
                org_id: oragID,
                sub_org_id: subOragID,
                connection_id: connectionId
            })
            .then(() => { if (!cancelled) setHealth('ok'); })
            .catch(() => { if (!cancelled) setHealth('down'); });
        return () => { cancelled = true; };
    }, [connectionId, oragID, subOragID]);

    function openPicker() { refresh(); setPickerOpen(true); }
    function openNew() {
        setEditingConnection(null);
        setFormDraft({
            ...EMPTY_DRAFT
        });
        setFormMode('create');
        setFormOpen(true);
    }
    function openEdit() {
        if (!selected) return;
        setEditingConnection(selected);
        setFormDraft({
            connection_name: selected.connection_name || '',
            description: selected.description || '',
            server_url: selected.server_url || '',
            transport: selected.transport || 'streamable_http',
            timeout_ms: selected.timeout_ms || 30000,
            auth: selected.auth || {
                type: 'none'
            },
            headers: selected.headers || {}
        });
        setFormMode('edit');
        setFormOpen(true);
    }

    function pickFromList(c) {
        onChange({
            connection_id: c.connection_id,
            ...c
        });
        setPickerOpen(false);
    }

    /** MCPForm persists internally; we just refresh + select. */
    async function handleSaved(savedConn) {
        try {
            const arr = await mcpConnectionService.listMCPConnections(oragID, subOragID);
            setList(Array.isArray(arr) ? arr : []);
            const match = savedConn?.connection_id
                ? (Array.isArray(arr) ? arr : []).find((c) => c.connection_id === savedConn.connection_id)
                : (Array.isArray(arr) ? arr : []).find(
                    (c) => c.connection_name === formDraft.connection_name
                );
            if (match) onChange({
                connection_id: match.connection_id,
                ...match
            });
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Refresh failed.');
        }
        setFormOpen(false);
    }

    return (
        <div>
            <div className="ds-pick-chip">
                <span className="ds-pick-chip-icon ds-tone-orange">
                    <Cog size={15} />
                </span>
                <div className="ds-pick-chip-body">
                    {selected ? (
                        <>
                            <div className="ds-pick-chip-title flex items-center gap-2">
                                <span
                                    title={
                                        health === 'ok' ? 'Reachable — discovery succeeded'
                                            : health === 'down' ? 'Unreachable — discovery failed'
                                                : health === 'checking' ? 'Checking…'
                                                    : 'Not yet checked'
                                    }
                                    className={cn(
                                        'inline-block h-2 w-2 rounded-full shrink-0',
                                        health === 'ok' && 'bg-green-500',
                                        health === 'down' && 'bg-red-500',
                                        health === 'checking' && 'bg-amber-400 animate-pulse',
                                        health === 'idle' && 'bg-gray-300'
                                    )}
                                />
                                {selected.connection_name || selected.connection_id}
                            </div>
                            <div className="ds-pick-chip-meta">
                                {selected.server_url || '—'}{selected.auth?.type ? ` · auth ${selected.auth.type}` : ''}
                            </div>
                        </>
                    ) : connectionId && loadingList ? (
                        <>
                            <div className="animate-pulse" style={{
                                height: 13, width: 150, borderRadius: 4, background: 'var(--ds-surface-3)'
                            }} />
                            <div className="animate-pulse" style={{
                                height: 10, width: 210, borderRadius: 4, background: 'var(--ds-surface-3)', marginTop: 6
                            }} />
                        </>
                    ) : connectionId ? (
                        <>
                            <div className="ds-pick-chip-title" style={{
                                fontFamily: 'var(--ds-font-mono)'
                            }}>{connectionId}</div>
                            <div className="ds-pick-chip-meta" style={{
                                fontStyle: 'italic'
                            }}>not in your library</div>
                        </>
                    ) : (
                        <div className="ds-pick-chip-meta" style={{
                            fontStyle: 'italic'
                        }}>No MCP connection selected.</div>
                    )}
                </div>
                <div className="ds-pick-chip-actions">
                    <Button variant="outline" size="sm" type="button" onClick={openPicker}>Change</Button>
                    <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        onClick={openEdit}
                        disabled={!selected}
                        title={selected ? 'Edit this MCP connection' : 'Pick or create one first'}
                    >
                        <Pencil className="mr-1 h-3 w-3" /> Edit
                    </Button>
                    <Button size="sm" type="button" onClick={openNew}>
                        <Plus className="mr-1 h-3 w-3" /> New
                    </Button>
                </div>
            </div>
            <p className="ds-help" style={{
                marginTop: 6
            }}>
                Saved into your sub-org's Library — visible under <b>Library → MCP Servers</b>.
            </p>
            {error && (
                <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>{error}</div>
                </div>
            )}

            {/* Picker dialog */}
            <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Pick an MCP connection</DialogTitle>
                        <DialogDescription>
                            Lists every MCP connection in this sub-org's Library. Pick one or create new.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={refresh} disabled={loadingList}>
                            <RefreshCw className={cn('mr-1 h-3 w-3', loadingList && 'animate-spin')} /> Refresh
                        </Button>
                        <Button size="sm" onClick={() => { setPickerOpen(false); openNew(); }}>
                            <Plus className="mr-1 h-3 w-3" /> New connection
                        </Button>
                        <span className="ml-auto text-xs text-muted-foreground">{list.length} in library</span>
                    </div>
                    <div className="max-h-[60vh] overflow-y-auto rounded border">
                        {loadingList && (
                            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                            </div>
                        )}
                        {!loadingList && list.length === 0 && (
                            <div className="p-6 text-center text-sm text-muted-foreground">
                                No MCP connections in this sub-org yet.
                            </div>
                        )}
                        {!loadingList && list.map((c) => {
                            const sel = c.connection_id === connectionId;
                            return (
                                <button
                                    key={c.connection_id}
                                    type="button"
                                    onClick={() => pickFromList(c)}
                                    className={cn('ds-pick-row', sel && 'ds-pick-row-selected')}
                                >
                                    <span className="ds-pick-row-icon ds-tone-orange">
                                        <Cog size={14} />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="truncate font-semibold text-sm">{c.connection_name || c.connection_id}</span>
                                            <span className="ds-badge ds-badge-warning">
                                                {c.auth?.type || 'none'}
                                            </span>
                                            {sel && <Check size={13} style={{
                                                color: 'var(--ds-primary)'
                                            }} />}
                                        </div>
                                        <div className="ds-pick-chip-meta" style={{
                                            marginTop: 2
                                        }}>
                                            {c.server_url || '—'}
                                        </div>
                                        {c.description && (
                                            <div style={{
                                                marginTop: 4,
                                                fontSize: 'var(--ds-text-xs)',
                                                color: 'var(--ds-text-muted)',
                                                lineHeight: 1.5,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden'
                                            }}>
                                                {c.description}
                                            </div>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </DialogContent>
            </Dialog>

            {/* MCPForm — wrap in our Dialog (it's a flat form, not a Dialog itself). */}
            <Dialog open={formOpen} onOpenChange={setFormOpen}>
                <DialogContent
                    className={cn(
                        'flex flex-col gap-0 overflow-hidden p-0',
                        showTest
                            ? 'h-[88vh] max-h-[88vh] w-[95vw] max-w-[1400px]'
                            : 'max-h-[88vh] max-w-3xl'
                    )}
                >
                    <DialogHeader className="flex-shrink-0 border-b px-6 pb-3 pt-5 pr-12">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0 flex-1">
                                <DialogTitle className="flex flex-wrap items-center gap-2">
                                    {formMode === 'edit' ? 'Edit MCP connection' : 'New MCP connection'}
                                    {showTest && !pristine && (
                                        <span className="rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-900">
                                            Unsaved
                                        </span>
                                    )}
                                </DialogTitle>
                                <DialogDescription>
                                    Saved into <b>Library → MCP Servers</b> for this sub-org.
                                </DialogDescription>
                            </div>
                            <div className="flex flex-shrink-0 items-center gap-1.5">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowJson(true)}
                                    title="View JSON"
                                    className="h-8"
                                >
                                    <Code className="mr-1 h-3 w-3" />
                                    JSON
                                </Button>
                                <Button
                                    type="button"
                                    variant={showTest ? 'secondary' : 'outline'}
                                    size="sm"
                                    onClick={() => setShowTest((v) => !v)}
                                    title={showTest ? 'Hide test panel' : 'Open test panel'}
                                    className="h-8"
                                >
                                    <Play className="mr-1 h-3 w-3" />
                                    {showTest ? 'Hide test' : 'Test'}
                                </Button>
                                {showTest && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setTestMaximized((v) => !v)}
                                        title={testMaximized ? 'Restore split view' : 'Maximize test panel'}
                                        className="h-8 px-2"
                                    >
                                        {testMaximized
                                            ? <Minimize2 className="h-4 w-4" />
                                            : <Maximize2 className="h-4 w-4" />}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </DialogHeader>
                    <div
                        className={cn(
                            'flex min-h-0 flex-1 overflow-hidden',
                            showTest && 'flex-row'
                        )}
                    >
                        <div
                            className={cn(
                                'min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4',
                                showTest && !testMaximized && 'min-w-0 max-w-[50%] border-r',
                                showTest && testMaximized && 'hidden'
                            )}
                        >
                            {formOpen && (
                                <MCPForm
                                    mode={formMode}
                                    orgId={oragID}
                                    subOrgId={subOragID}
                                    connectionId={editingConnection?.connection_id || null}
                                    existing={editingConnection || null}
                                    draft={formDraft}
                                    onDraftChange={setFormDraft}
                                    onSaved={handleSaved}
                                    onCancel={() => setFormOpen(false)}
                                />
                            )}
                        </div>
                        {showTest ? (
                            <div
                                className={cn(
                                    'flex min-h-0 flex-1 flex-col overflow-hidden',
                                    !testMaximized && 'min-w-0'
                                )}
                            >
                                {!pristine ? (
                                    <div className="flex-shrink-0 border-b border-amber-300/50 bg-amber-50/60 px-3 py-1.5 text-[11px] text-amber-900">
                                        <AlertCircle className="mr-1 inline h-3 w-3" />
                                        Unsaved edits — discover/invoke runs against the saved version. Save first to test the latest.
                                    </div>
                                ) : null}
                                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                                    <McpTestPanel mcp={mcpForTest} maximized={testMaximized} />
                                </div>
                            </div>
                        ) : null}
                    </div>
                </DialogContent>
            </Dialog>
            <EntityJsonView
                open={showJson}
                onOpenChange={setShowJson}
                title="MCP connection JSON"
                entity={formDraft}
                onApply={(parsed) => {
                    if (!parsed || typeof parsed !== 'object') return;
                    setFormDraft(parsed);
                }}
            />
        </div>
    );
}
